import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowUpRight, Loader2, Award, Target, Mail, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LOGO, FOUNDER, CONTACT_EMAIL, BOOKING_URL } from "@/lib/brand";

const fade = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 } };

function ScoreBlock({ label, value, testid }) {
  return (
    <div className="bg-white border border-[var(--brdr)] rounded-sm p-8 md:p-10 shadow-sm text-center">
      <div className="overline">{label}</div>
      <div className="mt-4 flex items-end justify-center gap-1">
        <span className="font-serif text-7xl text-oxford leading-none" data-testid={testid}>
          {value === null || value === undefined ? "—" : value.toFixed(1)}
        </span>
        <span className="font-mono text-[var(--text2)] mb-2">/ 10</span>
      </div>
    </div>
  );
}

function Bar({ name, score, isLegacy }) {
  return (
    <div className="py-4 border-b border-[var(--brdr)] last:border-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-oxford font-medium">{name}</span>
        <span className="font-mono text-sm text-oxford">{score?.toFixed(1)}</span>
      </div>
      <div className="h-1 bg-[var(--brdr)] rounded-full overflow-hidden">
        <motion.div className={`h-full ${isLegacy ? "bg-gold" : "bg-oxford"}`}
          initial={{ width: 0 }} animate={{ width: `${(score / 10) * 100}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }} />
      </div>
    </div>
  );
}

export default function Result() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/assessment/${id}/result`);
        setData(res.data);
      } catch (e) {
        toast.error("Result not available.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/assessment/${id}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Aikyam_Scorecard_${data.report_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Unable to download report.");
    } finally {
      setDownloading(false);
    }
  };

  const emailMe = async () => {
    setEmailing(true);
    try {
      const res = await api.post(`/assessment/${id}/email`);
      if (res.data.mocked) {
        toast.success("Your report is on its way (email delivery is being finalised).");
      } else if (res.data.sent) {
        toast.success(`Your scorecard has been emailed to ${res.data.to}.`);
      } else {
        toast.error("Could not send the email just now. You can download the PDF instead.");
      }
    } catch (e) {
      toast.error("Could not send the email. Please download the PDF instead.");
    } finally {
      setEmailing(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-alabaster"><Loader2 className="h-6 w-6 animate-spin text-oxford" /></div>;
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-alabaster">
      <p className="text-[var(--text2)]">This result is not available.</p>
    </div>
  );

  const { scores, respondent, report_id } = data;
  const businessComps = scores.components.filter((c) => !c.is_legacy).sort((a, b) => a.order - b.order);
  const legacyComps = scores.components.filter((c) => c.is_legacy);

  return (
    <div className="min-h-screen bg-alabaster">
      <header className="border-b border-[var(--brdr)] bg-white no-print">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><img src={LOGO} alt="Aikyam" className="h-8 w-auto" /></Link>
          <span className="font-mono text-xs text-[var(--text2)]">{report_id}</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-14 md:py-20">
        <motion.div {...fade} transition={{ duration: 0.5 }}>
          <div className="overline mb-3">Your Scorecard</div>
          <h1 className="font-serif text-4xl md:text-5xl text-oxford tracking-tight">{respondent.name}</h1>
          <p className="mt-2 text-[var(--text2)]">{respondent.company} · {respondent.sector}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <ScoreBlock label="Business Score" value={scores.business_score} testid="score-business" />
          <ScoreBlock label="Legacy Score" value={scores.legacy_score} testid="score-legacy" />
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button data-testid="download-report-btn" onClick={download} disabled={downloading}
            className="btn-primary text-white h-12 px-8 no-print">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-2 h-4 w-4" /> Download PDF Scorecard</>}
          </Button>
          <Button data-testid="email-report-btn" onClick={emailMe} disabled={emailing}
            className="btn-outline h-12 px-8 no-print">
            {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-2 h-4 w-4" /> Email me my report</>}
          </Button>
        </div>

        {/* Component scores */}
        <div className="grid lg:grid-cols-3 gap-6 mt-14">
          <div className="lg:col-span-2 bg-white border border-[var(--brdr)] rounded-sm p-8 shadow-sm">
            <h3 className="font-serif text-2xl text-oxford mb-2">Component Scores</h3>
            <p className="text-sm text-[var(--text2)] mb-4">Business dimensions</p>
            <div>{businessComps.map((c) => <Bar key={c.name} {...c} score={c.score} />)}</div>
            {legacyComps.length > 0 && (
              <>
                <p className="text-sm text-[var(--text2)] mt-6 mb-2">Legacy dimension</p>
                {legacyComps.map((c) => <Bar key={c.name} {...c} score={c.score} />)}
              </>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-oxford text-white rounded-sm p-8 shadow-sm">
              <Award className="h-6 w-6 text-gold" strokeWidth={1.5} />
              <h4 className="font-serif text-xl mt-4">Strongest Areas</h4>
              <ul className="mt-3 space-y-2 text-sm text-white/85" data-testid="strongest-areas">
                {scores.strongest_areas.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
            <div className="bg-white border border-[var(--brdr)] rounded-sm p-8 shadow-sm">
              <Target className="h-6 w-6 text-gold" strokeWidth={1.5} />
              <h4 className="font-serif text-xl mt-4 text-oxford">Priority Areas for Focus</h4>
              <ul className="mt-3 space-y-2 text-sm text-[var(--text2)]" data-testid="priority-areas">
                {scores.priority_areas.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* Interpretation */}
        <div className="bg-white border border-[var(--brdr)] rounded-sm p-8 md:p-10 shadow-sm mt-6">
          <h3 className="font-serif text-2xl text-oxford">Interpretation</h3>
          <p className="mt-4 text-[var(--text2)] leading-relaxed">{scores.interpretation.business}</p>
          {scores.interpretation.legacy && (
            <p className="mt-4 text-[var(--text2)] leading-relaxed">{scores.interpretation.legacy}</p>
          )}
        </div>

        {/* Founder / booking */}
        <div className="relative overflow-hidden bg-oxford text-white rounded-sm mt-6 grain no-print" data-testid="founder-cta">
          <div className="relative grid md:grid-cols-[260px_1fr]">
            <div className="h-64 md:h-auto">
              <img src={FOUNDER.photo} alt={FOUNDER.name} className="w-full h-full object-cover object-top" />
            </div>
            <div className="p-8 md:p-12">
              <div className="overline text-gold mb-3">Speak with Aikyam</div>
              <h3 className="font-serif text-3xl md:text-4xl">Book a discovery call with {FOUNDER.name}</h3>
              <p className="mt-1 text-sm text-white/70">{FOUNDER.title}</p>
              <p className="mt-5 text-white/80 leading-relaxed text-sm md:text-base">{FOUNDER.bio}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={BOOKING_URL || `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Discovery call — Aikyam Business Evaluation")}&body=${encodeURIComponent(`Hi Madhavan,\n\nI've just completed the Aikyam Business Evaluation (Report ${report_id}) and would like to book a discovery call.\n\nThank you.`)}`}
                  target={BOOKING_URL ? "_blank" : undefined} rel="noreferrer"
                  data-testid="book-call-btn"
                  className="inline-flex items-center gap-2 bg-gold text-oxford h-12 px-7 rounded-sm font-semibold">
                  <CalendarClock className="h-4 w-4" /> Book a discovery call
                </a>
                <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-cta"
                  className="inline-flex items-center gap-2 border border-white/40 text-white h-12 px-7 rounded-sm hover:bg-white/10 transition-colors">
                  <Mail className="h-4 w-4" /> Email Madhavan
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
